import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX } = FiIcons;

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const images = [
    'https://i.ibb.co/zWmHhJwx/image-1.jpg',
    'https://i.ibb.co/4wpyrn29/image-2.jpg',
    'https://i.ibb.co/8LyqXkGv/image-3.jpg',
    'https://i.ibb.co/Z6W7fdm7/image-4.jpg',
    'https://i.ibb.co/4ydVWkS/image-5.jpg',
    'https://i.ibb.co/W4fp9cL6/image-6.jpg',
    'https://i.ibb.co/0ySVGsGG/image-7.jpg',
    'https://i.ibb.co/LdCGqnLg/image-8.jpg',
    'https://i.ibb.co/ym9LRRx2/image-9.jpg',
    'https://i.ibb.co/prL5sVnt/image-10.jpg',
    'https://i.ibb.co/JjpcPXz7/image-11.jpg',
    'https://i.ibb.co/d0vBWPpf/image-12.jpg',
    'https://i.ibb.co/h3z53fY/image-13.jpg',
    'https://i.ibb.co/RTgrx2rd/image-14.jpg',
    'https://i.ibb.co/1Yn6kmtr/image-15.jpg',
    'https://i.ibb.co/q3fgRFsG/image-16.jpg',
    'https://i.ibb.co/PZdytRq3/image-17.jpg',
    'https://i.ibb.co/BVkS73PV/image-18.jpg',
    'https://i.ibb.co/wrYTHqCG/image-19.jpg',
    'https://i.ibb.co/MDQJ9rBq/image-20.jpg',
    'https://i.ibb.co/WW8PLwxn/image-21.jpg',
    'https://i.ibb.co/HfGzbKF0/image-22.jpg',
    'https://i.ibb.co/60gSJt0s/image-23.jpg',
    'https://i.ibb.co/27bhZ4Cx/image-24.jpg',
    'https://i.ibb.co/xtFzDhmW/image-25.jpg',
    'https://i.ibb.co/ksS5K5mn/image-26.jpg',
    'https://i.ibb.co/CKbKCfQd/image-27.jpg'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-[#826046] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Our Work Gallery</h1>
            <p className="text-xl max-w-3xl mx-auto">
              Discover the quality of our work through these images showcasing our expertise in men's grooming
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {images.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: (index % 10) * 0.05 }}
                viewport={{ once: true }}
                className="aspect-square overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                  <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium">
                    Click to view
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-4xl max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-4 -right-4 bg-white text-black rounded-full p-2 hover:bg-gray-200 transition-colors duration-200 z-10"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
              <img
                src={selectedImage}
                alt="Gallery enlarged"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Ready to Look This Good?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join our satisfied customers and experience the quality that speaks for itself
            </p>
            <a
              href="tel:+971501656278"
              className="bg-[#826046] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#6d4d37] transition-colors duration-300 inline-block"
            >
              Book Your Appointment: +971 50 165 6278
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;